import { T } from '@tolgee/react';
import styled, { css } from 'styled-components';

import EnterprisePlanIcon from '../../../assets/icons/EnterprisePlanIcon';
import theme from '../../../assets/theme';

/**
 * SchoolPlanBadge — the orange pill that marks content as belonging to the
 * School plan tier. Displays the Curipod crown icon (EnterprisePlanIcon) plus
 * the literal text "School plan" so US teachers can read the tier at a glance
 * without decoding a color or icon alone.
 *
 * The pill itself is a tier marker — it does NOT carry any state info (trial
 * count, free-slice status, etc.). For state info, use `SchoolPlanCardFooter`
 * on the card or the sidebar `UsageCountersWidget`.
 *
 * Variants:
 * - `compact` — used in card corners and dropdown rows. Smaller crown, shorter padding.
 * - `full`    — used in paywall headers and standalone marketing surfaces.
 *
 * States:
 * - `active` — default orange
 * - `muted`  — desaturated grey, used when the user has exhausted their trial
 *              and the content they're looking at has NO free slice. When the
 *              content does have a free slice, keep it `active` per the workshop
 *              rule: teachers shouldn't read "locked" on content they can still use.
 */
export type SchoolPlanBadgeProps = {
  variant?: 'compact' | 'full';
  state?: 'active' | 'muted';
  showLabel?: boolean;
  className?: string;
};

export function SchoolPlanBadge({
  variant = 'compact',
  state = 'active',
  showLabel = true,
  className,
}: SchoolPlanBadgeProps) {
  const iconSize = variant === 'compact' ? 14 : 20;
  return (
    <Pill $variant={variant} $state={state} className={className}>
      <IconWrap $size={iconSize}>
        <EnterprisePlanIcon
          color="white"
          width={iconSize}
          height={iconSize}
          style={{ display: 'block' }}
        />
      </IconWrap>
      {showLabel && (
        <Label $variant={variant}>
          <T keyName="SchoolPlanBadge.Label" defaultValue="School plan" />
        </Label>
      )}
    </Pill>
  );
}

const Pill = styled.span<{
  $variant: 'compact' | 'full';
  $state: 'active' | 'muted';
}>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 20px;
  color: white;
  font-weight: 700;
  letter-spacing: 0.1px;
  white-space: nowrap;
  box-shadow: 0 2px 4px rgba(160, 80, 20, 0.18);

  ${({ $variant }) =>
    $variant === 'compact'
      ? css`
          padding: 4px 10px 4px 7px;
          font-size: 11px;
        `
      : css`
          padding: 6px 14px 6px 10px;
          font-size: 14px;
        `}

  ${({ $state }) =>
    $state === 'active'
      ? css`
          background: ${theme.colors.orange};
        `
      : css`
          background: ${theme.colors.locked};
          box-shadow: none;
        `}
`;

const IconWrap = styled.span<{ $size: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  svg {
    width: 100%;
    height: 100%;
  }
`;

const Label = styled.span<{ $variant: 'compact' | 'full' }>`
  line-height: 1;
  padding-top: 1px; /* optical alignment against the crown */
`;
